import React from 'react';
import {
  Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField,
} from '@mui/material';
import { employmentService } from '../../services/api';
import { useConfirm } from '../Common/ConfirmDialog';
import { useToast } from '../Common/ToastProvider';
import { apiErrorText, fmtDate, isoToday } from './salaryFormat';

// S3 — record (or cancel) the end of an employment. Until the termination date the employee stays
// active and shows "Leaving <date>"; from that date the server sets the status to Terminated.
export default function TerminateDialog({ open, employee, fullName, onClose, onSaved }) {
  const toast = useToast();
  const confirm = useConfirm();
  const existing = employee?.terminationDate ? employee : null;
  const [form, setForm] = React.useState({});
  const [bases, setBases] = React.useState([]);
  const [preview, setPreview] = React.useState(null);
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setError('');
    setForm({
      terminationDate: (existing?.terminationDate || '').substring(0, 10),
      legalBasis: existing?.terminationLegalBasis || '',
      requestDate: '', orderNumber: '', orderDate: '', documentPath: '', note: '',
    });
    employmentService.getLegalBases().then((b) => setBases(Array.isArray(b) ? b : [])).catch(() => setBases([]));
  }, [open, existing]);

  React.useEffect(() => {
    if (!open || !employee?.id || !form.terminationDate) { setPreview(null); return; }
    let active = true;
    employmentService.terminationPreview(employee.id, form.terminationDate)
      .then((p) => { if (active) setPreview(p); })
      .catch(() => { if (active) setPreview(null); });
    return () => { active = false; };
  }, [open, employee?.id, form.terminationDate]);

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));
  const future = form.terminationDate && form.terminationDate > isoToday();

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await employmentService.terminate(employee.id, {
        terminationDate: form.terminationDate, legalBasis: form.legalBasis,
        requestDate: form.requestDate || null, orderNumber: form.orderNumber || null, orderDate: form.orderDate || null,
        documentPath: form.documentPath || null, note: form.note || null,
      });
      toast('Termination recorded', { variant: 'success' });
      onSaved?.();
    } catch (err) {
      setError(apiErrorText(err, 'Could not record the termination.'));
    } finally {
      setSaving(false);
    }
  };

  const cancelTermination = async () => {
    if (!(await confirm({ message: 'Remove the recorded termination? A Terminated status returns to Active.', danger: true, confirmLabel: 'Remove' }))) return;
    try {
      await employmentService.cancelTermination(employee.id);
      toast('Termination removed', { variant: 'success' });
      onSaved?.();
    } catch (err) {
      setError(apiErrorText(err, 'Could not remove the termination.'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Terminate employment · {fullName}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {existing && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Termination already recorded from {fmtDate(existing.terminationDate)} ({existing.terminationLegalBasis}). Saving replaces it.
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <TextField id="term-date" fullWidth required type="date" label="Terminated from" InputLabelProps={{ shrink: true }}
              value={form.terminationDate || ''} onChange={set('terminationDate')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete id="term-basis" freeSolo options={bases} value={form.legalBasis || ''}
              onChange={(e, v) => setForm((f) => ({ ...f, legalBasis: v || '' }))}
              onInputChange={(e, v) => setForm((f) => ({ ...f, legalBasis: v || '' }))}
              renderInput={(params) => <TextField {...params} required label="Legal basis" />} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField id="term-request" fullWidth type="date" label="Employee request dated" InputLabelProps={{ shrink: true }}
              value={form.requestDate || ''} onChange={set('requestDate')} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField id="term-order-no" fullWidth label="Order No." value={form.orderNumber || ''} onChange={set('orderNumber')} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField id="term-order-date" fullWidth type="date" label="Order date" InputLabelProps={{ shrink: true }}
              value={form.orderDate || ''} onChange={set('orderDate')} />
          </Grid>
          <Grid item xs={12}>
            <TextField id="term-path" fullWidth label="Order in dossier (path)" value={form.documentPath || ''} onChange={set('documentPath')} />
          </Grid>
          <Grid item xs={12}>
            <TextField id="term-note" fullWidth label="Note" value={form.note || ''} onChange={set('note')} />
          </Grid>
        </Grid>
        {preview && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Unused paid leave {preview.year}: {preview.unusedPaidLeaveDays} day(s)
            {preview.pendingPaidLeaveDays > 0 ? ` (${preview.pendingPaidLeaveDays} more pending approval)` : ''} — for the final settlement under чл. 224 КТ.
          </Alert>
        )}
        {form.terminationDate && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {future
              ? `Until ${fmtDate(form.terminationDate)} the status stays Active with “Leaving ${fmtDate(form.terminationDate)}”. From that date it becomes Terminated; the history stays.`
              : 'The date has passed: the status becomes Terminated now; the history stays.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {existing && <Button color="warning" onClick={cancelTermination} sx={{ mr: 'auto' }}>Remove termination</Button>}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" disabled={!form.terminationDate || !form.legalBasis || saving} onClick={save}>
          Record termination
        </Button>
      </DialogActions>
    </Dialog>
  );
}
